// The page the server sends in the HTML (the backend's web/share_meta.php: <div id="server-page"> in
// #root, with the header and footer) stays on screen as delivered while this app starts: the app
// draws its own page behind it, unseen, and takes its place in one step once that page has
// everything the server's copy shows - no API request open and nothing drawn for a moment - or
// after MAX_WAIT_MS, or as soon as someone clicks, types or tabs into the copy. Before this the
// copy was hidden until the app had drawn the page again, so a page showed nothing until the
// app's script had loaded and run (QA B46). A copy the server hides (display: none: the screens
// whose copy is text for search engines only) is replaced on the app's first draw, as before.

// Nothing open and nothing new drawn for this long: the app's page is complete
const QUIET_MS = 200;
// From the app's start: the app's page is shown even if something is still loading
const MAX_WAIT_MS = 4000;
// Requests that are not the page's own data (tracking scripts)
const NOT_PAGE_DATA = /google|facebook|doubleclick|bing\.com|licdn|linkedin|reddit|hotjar|clarity\.ms/i;
// Using the copy: the app's page at once
const USES = ['click', 'keydown', 'focusin'];

// Counts the fetch and XMLHttpRequest requests open from now on: { open() = how many, stop() }
const countRequests = () => {
    let open = 0;
    const counted = (url) => !NOT_PAGE_DATA.test(String(url || ''));
    const fetch = window.fetch;
    const countedFetch = function countedFetch(input, init) {
        const request = fetch.apply(this, arguments);
        if (counted(typeof input === 'string' ? input : input && input.url)) {
            open += 1;
            const done = () => { open -= 1; };
            request.then(done, done);
        }
        return request;
    };
    if (fetch) {
        window.fetch = countedFetch;
    }
    const XHR = window.XMLHttpRequest && window.XMLHttpRequest.prototype;
    const xhrOpen = XHR && XHR.open;
    const xhrSend = XHR && XHR.send;
    if (XHR) {
        XHR.open = function countedOpen(method, url) {
            this.countedRequest = counted(url);
            return xhrOpen.apply(this, arguments);
        };
        XHR.send = function countedSend() {
            if (this.countedRequest) {
                open += 1;
                this.addEventListener('loadend', () => { open -= 1; }, { once: true });
            }
            return xhrSend.apply(this, arguments);
        };
    }
    return {
        open: () => open,
        // Back to the browser's own, unless something else has replaced them since
        stop: () => {
            if (fetch && window.fetch === countedFetch) {
                window.fetch = fetch;
            }
            if (XHR) {
                XHR.open = xhrOpen;
                XHR.send = xhrSend;
            }
        },
    };
};

// The element the app should draw into: `root` itself when there is no server copy to keep on
// screen, else a new element in `root`, unseen beside the copy, that becomes the page when the
// app's own page is ready (see above)
export const appContainer = (root) => {
    const served = document.getElementById('server-page');
    if (!served || served.parentNode !== root || window.getComputedStyle(served).display === 'none') {
        return root;
    }
    const requests = countRequests();
    const app = document.createElement('div');
    // Where the copy starts and as wide, out of the page's flow, unseen, not clickable or
    // focusable, and adding nothing to the page's height (overflow: clip). Unseen by its opacity,
    // which its elements do not inherit: an inherited change (visibility) would set off the
    // page's transitions (the phone menu button's lines) when it is shown.
    const at = root.getBoundingClientRect();
    Object.assign(app.style, {
        position: 'absolute', top: `${at.top + window.scrollY}px`, left: `${at.left + window.scrollX}px`,
        width: `${at.width}px`, height: '0', overflow: 'clip', opacity: '0', pointerEvents: 'none',
    });
    app.setAttribute('aria-hidden', 'true');
    app.inert = true;
    // Before the copy, which has the same ids: the app's own lookups by id
    // (document.getElementById("marquee-horizontal") in Body.js) find its elements first
    root.insertBefore(app, root.firstChild);

    const started = performance.now();
    let drawn = false;
    let lastDrawn = started;
    const observer = new MutationObserver(() => {
        drawn = true;
        lastDrawn = performance.now();
    });
    observer.observe(app, { childList: true, subtree: true, characterData: true });

    let timer = null;
    function handOver() {
        if (!served.isConnected) {
            return;
        }
        clearInterval(timer);
        observer.disconnect();
        requests.stop();
        USES.forEach((type) => served.removeEventListener(type, onUse, true));
        served.remove();
        app.removeAttribute('style');
        app.removeAttribute('aria-hidden');
        app.inert = false;
    }
    timer = setInterval(() => {
        const now = performance.now();
        if (now - started >= MAX_WAIT_MS || (drawn && requests.open() === 0 && now - lastDrawn >= QUIET_MS)) {
            handOver();
        }
    }, 50);

    // Someone using the copy (other than following one of its links, which the browser opens):
    // the app's page at once, with the field they went to in focus there, and a click given to
    // what the app has at that spot (the phone menu's button, "#" links that open its submenus)
    function onUse(event) {
        const target = event.target instanceof Element ? event.target : null;
        const link = target && target.closest('a[href]');
        if (event.type === 'click' && link && !/^#?$/.test(link.getAttribute('href'))) {
            return;
        }
        handOver();
        if (event.type === 'click') {
            event.preventDefault();
            event.stopPropagation();
            const there = document.elementFromPoint && document.elementFromPoint(event.clientX, event.clientY);
            if (there && app.contains(there)) {
                there.click();
            }
            return;
        }
        const field = target ? target.closest('input, select, textarea') : null;
        const escape = (value) => (window.CSS && CSS.escape ? CSS.escape(value) : value);
        const selector = field && (field.id ? `#${escape(field.id)}` : field.name ? `[name="${escape(field.name)}"]` : null);
        const same = selector && app.querySelector(selector);
        if (same) {
            same.focus();
        }
    }
    USES.forEach((type) => served.addEventListener(type, onUse, true));
    return app;
};
